import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { Drawer, Field, UpgradeBanner } from "../components/UI";
import { Page, Skeleton } from "../components/AppShell";
import { TagPicker } from "../components/TagUI";
import JobMiniPanel from "../components/JobMiniPanel";
import CustomerSearchInput from "../components/CustomerSearchInput";
import UserSearchInput from "../components/UserSearchInput";
import CaseSearchInput from "../components/CaseSearchInput";
import LocationSearchInput from "../components/LocationSearchInput";
import SubjectSearchInput from "../components/SubjectSearchInput";
import CustomerFormDrawer from "../components/CustomerFormDrawer";
import LocationManagerDrawer from "../components/OrgAdmin/LocationManagerDrawer";
import useBulkSelect from "../lib/useBulkSelect";
import { Plus, Briefcase, MagnifyingGlass, Warning, CheckCircle, FileText, Trash } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

/* ---------------------------------------------------------------------------
 * Phase 3: "+ NEW" inline chain on the New Job drawer.
 * Each select gets a small +NEW pill that opens a stacked sub-drawer.
 * On save, the new record is auto-selected in the job form.
 * For Assigned-To, the sub-drawer offers a toggle between Internal User
 * (creates an org invitation) and Contractor Org (creates a vendor_link).
 * For Notes, the picker lists saved note_templates + supports "+ NEW TEMPLATE".
 * -------------------------------------------------------------------------*/
const ASSIGNEE_KIND_USER = "user";
const ASSIGNEE_KIND_CONTRACTOR = "contractor";

export default function Jobs() {
  const { user } = useAuth();
  const isContractor = user?.org_type === "contractor";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  // iter 40 — Service-speed presets for the new-job drawer (filtered by job_type_id).
  const [speedPresets, setSpeedPresets] = useState([]);
  // iter 40 — Job-category tags available in the org (for the drawer's multi-select).
  const [jobTags, setJobTags] = useState([]);
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  // Allow other parts of the app (TopBar quick-create button, ⌘J / ⌘., deep
  // links) to pop the New Job drawer via `?new=1`. Reacts to every change
  // of the query param so repeated triggers from an already-mounted page
  // re-open the drawer — fixes the bug where second-click did nothing after
  // the URL had already been navigated to once.
  const [searchParams, setSearchParams] = useSearchParams();
  const newFlag = searchParams.get("new");
  useEffect(() => {
    if (newFlag !== "1") return;
    // Defer the two state updates to a microtask so the strict
    // react-hooks set-state-in-effect lint stays happy.
    queueMicrotask(() => {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    });
  }, [newFlag, searchParams, setSearchParams]);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");

  // ---- Phase 3 sub-drawer state ----
  // Each sub-drawer's `open` flag lives separately so users can flip among them.
  // On save, we refresh the source list and auto-set the parent job form field.
  const [newCase, setNewCase] = useState(null);     // { case_number, plaintiff, defendant } | null
  const [newLoc, setNewLoc] = useState(null);       // { name, address1, city, state } | null
  const [newCust, setNewCust] = useState(null);     // { name, email, phone, tags: [] } | null (creates customer at org-level)
  const [newAssignee, setNewAssignee] = useState(null); // { kind, name, email, role, org_name, contact_name, contact_email, contact_phone, send_invite, tags }
  const [notePicker, setNotePicker] = useState(false);    // boolean — open Note Templates picker
  const [templates, setTemplates] = useState([]);
  const [tplNew, setTplNew] = useState(null);             // { title, body } | null (inline create form inside picker)
  const [subBusy, setSubBusy] = useState(false);
  const [subErr, setSubErr] = useState("");
  const [subOk, setSubOk] = useState("");

  // Subject-picker modal state — driven from the row "+ SUBJECT" action.
  const [pickerJob, setPickerJob] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerErr, setPickerErr] = useState("");
  const [savingPick, setSavingPick] = useState(false);

  // Job mini-panel state — opened by clicking the SUBJ count.
  const [detailJobId, setDetailJobId] = useState(null);

  // iter 35 — list-page filters (typeahead-driven).
  //  Each entry can be null (no filter) or {id, label} from the matching
  //  search input. The chip is shown in the filter bar with a clear button.
  const [filters, setFilters] = useState({
    case: null, customer: null, subject: null, location: null, assignee: null,
  });
  const clearFilter = (k) => setFilters(f => ({ ...f, [k]: null }));
  const anyFilter = !!(filters.case || filters.customer || filters.subject || filters.location || filters.assignee);
  const clearAllFilters = () => setFilters({ case: null, customer: null, subject: null, location: null, assignee: null });
  // iter 37 — bulk-select foundation (no bulk actions yet).
  const bulk = useBulkSelect(items, { testid: "job-row" });

  // iter 40 — When the user picks a Job Type, refresh the available
  // service-speed presets so the drawer can render only the matching ones.
  useEffect(() => {
    if (!form.job_type_id) {
      queueMicrotask(() => setSpeedPresets([]));
      return;
    }
    api.get(`/service-speed-presets?job_type_id=${form.job_type_id}`)
      .then(r => queueMicrotask(() => setSpeedPresets(r.data || [])))
      .catch(() => queueMicrotask(() => setSpeedPresets([])));
  }, [form.job_type_id]);

  const load = () => {
    api.get("/jobs").then(r => setItems(r.data)).finally(() => setLoading(false));
    api.get("/jobs/types").then(r => setTypes(r.data));
    api.get("/tags?category=job").then(r => setJobTags(r.data || [])).catch(() => setJobTags([]));
    api.get("/jobs/statuses").then(r => setStatuses(r.data));
    api.get("/cases").then(r => setCases(r.data));
    api.get("/locations").then(r => setLocations(r.data));
    api.get("/users").then(r => setUsers(r.data));
    api.get("/customers?paginate=false").then(r => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => setCustomers([]));
    api.get("/subjects").then(r => setSubjects(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      // 1) Create the job with the basics — the rest is filled in on Job Dash.
      const r = await api.post("/jobs", {
        customer_id: form.customer_id || null,
        job_type_id: form.job_type_id || null,
        service_speed_preset_id: form.service_speed_preset_id || null,
        notes: form.notes || null,
      });
      const newJobId = r.data.id;

      // 2) Apply selected job tags (+ needs-review when an .eml is queued).
      const tagIds = (form.tags || []).map(t => t.id);
      if (form.emlFile) {
        const nr = jobTags.find(t => t.name.toLowerCase() === "needs-review");
        if (nr && !tagIds.includes(nr.id)) tagIds.push(nr.id);
      }
      if (tagIds.length) {
        try { await api.post(`/job/${newJobId}/tags`, { tag_ids: tagIds }); }
        catch (e) { console.warn("tag apply failed", e); }
      }

      // 3) If an .eml file was queued, hand it to the intake pipeline. The
      //    existing /intake/eml endpoint creates an `email_intakes` row in
      //    status=processing and the next AI tick fills in suggestions.
      if (form.emlFile) {
        const fd = new FormData();
        fd.append("file", form.emlFile);
        fd.append("job_id", newJobId);
        try { await api.post("/intake/eml", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e) { console.warn("eml intake failed", e); }
      }

      setOpen(false); setForm({}); navigate(`/job-dash/${newJobId}`);
    } catch (e) { setErr(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`); load();
  };

  // ---- Phase 3 sub-drawer save handlers ----
  const clearSub = () => { setSubErr(""); setSubOk(""); setSubBusy(false); };

  const saveNewCase = async () => {
    clearSub(); setSubBusy(true);
    try {
      const r = await api.post("/cases", {
        case_number: newCase.case_number || null,
        plaintiff: newCase.plaintiff || null,
        defendant: newCase.defendant || null,
      });
      const list = await api.get("/cases");
      setCases(list.data);
      setForm(f => ({ ...f, case_id: r.data.id }));
      setNewCase(null);
    } catch (e) { setSubErr(formatErr(e)); }
    finally { setSubBusy(false); }
  };

  const onNewLocationSaved = async (created) => {
    try {
      const list = await api.get("/locations");
      setLocations(list.data);
    } catch (_) { /* ignore */ }
    if (created?.id) {
      setForm(f => ({ ...f, service_location_id: created.id }));
    }
  };

  const onNewCustomerSaved = async (created) => {
    // Refresh customer list and auto-select the newly created customer on
    // the job form so the user can continue filling out the job seamlessly.
    try {
      const lst = await api.get("/customers?paginate=false");
      setCustomers(Array.isArray(lst.data) ? lst.data : (lst.data?.items || []));
    } catch (_) { /* ignore */ }
    if (created?.id) {
      setForm(f => ({ ...f, customer_id: created.id }));
      setSubOk(`Customer "${created.name}" linked to this job.`);
      setTimeout(() => clearSub(), 1500);
    }
  };

  const saveNewAssignee = async () => {
    clearSub(); setSubBusy(true);
    try {
      if (newAssignee.kind === ASSIGNEE_KIND_USER) {
        // iter 34 Phase H — the invite endpoint now pre-creates a
        // placeholder User row and returns its id, so we can drop the
        // new teammate straight into `assigned_user_id`. They'll be
        // marked active once they accept the email invite.
        const r = await api.post("/orgs/invitations", {
          email: newAssignee.email,
          role: newAssignee.role || "dispatcher",
          invite_type: "user",
        });
        const newUserId = r.data?.user_id;
        // Refresh user list so the placeholder appears in the dropdown.
        try {
          const lst = await api.get("/users");
          setUsers(lst.data);
        } catch (_) { /* ignore */ }
        if (newUserId) {
          setForm(f => ({ ...f, assigned_user_id: newUserId }));
          setSubOk(`Invite sent to ${newAssignee.email} — assigned to this job (will activate when they accept).`);
        } else {
          setSubOk(`Invite sent to ${newAssignee.email}. They'll appear in Assigned-To once they accept.`);
        }
      } else {
        // Create a vendor (contractor org).
        await api.post("/vendors", {
          org_name: newAssignee.org_name,
          contact_name: newAssignee.contact_name || null,
          contact_email: newAssignee.contact_email || null,
          contact_phone: newAssignee.contact_phone || null,
          send_invite: !!newAssignee.send_invite,
          tag_ids: (newAssignee.tags || []).map(t => t.id),
        });
        setSubOk(`Vendor "${newAssignee.org_name}" saved — visible on the Vendors page.`);
      }
      setTimeout(() => { setNewAssignee(null); clearSub(); }, 1500);
    } catch (e) { setSubErr(formatErr(e)); setSubBusy(false); }
  };

  // Note templates picker
  const openNotePicker = () => {
    setNotePicker(true);
    api.get("/note-templates").then(r => setTemplates(r.data)).catch(() => setTemplates([]));
  };
  const pickTemplate = (t) => {
    // Append body to current notes (separated by newline if existing).
    const current = (form.notes || "").trim();
    const next = current ? `${current}\n\n${t.body}` : t.body;
    setForm(f => ({ ...f, notes: next }));
    setNotePicker(false);
  };
  const saveNewTemplate = async () => {
    clearSub(); setSubBusy(true);
    try {
      const r = await api.post("/note-templates", tplNew);
      setTemplates(prev => [r.data, ...prev]);
      setTplNew(null);
    } catch (e) { setSubErr(formatErr(e)); }
    finally { setSubBusy(false); }
  };
  const deleteTemplate = async (id) => {
    if (!confirm("Delete this template?")) return;
    try { await api.delete(`/note-templates/${id}`); setTemplates(prev => prev.filter(t => t.id !== id)); }
    catch (e) { alert(formatErr(e)); }
  };

  const duplicate = async (id) => {
    if (!confirm("Duplicate this job?\n\n• Copies: type, status, location, assigned-to, price\n• Skips: invoices, calendar items, notes, service attempts, dates\n\nContinue?")) return;
    try {
      const r = await api.post(`/jobs/${id}/duplicate`, {});
      load();
      // Open the mini-panel on the new job so the user can immediately add subject/notes/dates.
      setDetailJobId(r.data.id);
    } catch (e) {
      alert(formatErr(e));
    }
  };

  // Count subjects tied to each job (for the "SUBJ" column on the table).
  const subjectsByJob = useMemo(() => {
    const m = {};
    subjects.forEach(s => { if (s.job_id) m[s.job_id] = (m[s.job_id] || 0) + 1; });
    return m;
  }, [subjects]);

  const subjectLabel = (s) =>
    [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ").trim() || s.aka || "(unnamed)";

  // iter 35 — client-side filter. The Subject filter uses subjects[] to map
  // subject_id → job_id(s) and keep only those jobs.
  const filteredItems = useMemo(() => {
    const subjectJobIds = filters.subject
      ? new Set(subjects.filter(s => s.id === filters.subject.id).map(s => s.job_id).filter(Boolean))
      : null;
    return items.filter(j => {
      if (filters.case && j.case_id !== filters.case.id) return false;
      if (filters.customer && j.customer_id !== filters.customer.id) return false;
      if (filters.location && j.service_location_id !== filters.location.id) return false;
      if (filters.assignee && j.assigned_user_id !== filters.assignee.id) return false;
      if (subjectJobIds && !subjectJobIds.has(j.id)) return false;
      return true;
    });
  }, [items, filters, subjects]);

  // Build the subject list shown in the picker:
  //   1. Pre-filter by free-text search (name / AKA / notes).
  //   2. Sort: same-case subjects first, then most recent.
  //   3. Hide subjects already attached to THIS job (no point re-cloning).
  const pickerSubjects = useMemo(() => {
    if (!pickerJob) return [];
    const q = pickerSearch.trim().toLowerCase();
    const filtered = subjects.filter(s => {
      if (s.job_id === pickerJob.id) return false;  // already on this job
      if (!q) return true;
      const hay = [s.first_name, s.middle_name, s.last_name, s.aka, s.notes].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    return filtered.sort((a, b) => {
      const aSame = a.case_id && a.case_id === pickerJob.case_id ? 0 : 1;
      const bSame = b.case_id && b.case_id === pickerJob.case_id ? 0 : 1;
      if (aSame !== bSame) return aSame - bSame;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [pickerJob, pickerSearch, subjects]);

  const sameCaseCount = useMemo(() => {
    if (!pickerJob || !pickerJob.case_id) return 0;
    return subjects.filter(s => s.case_id === pickerJob.case_id && s.job_id !== pickerJob.id).length;
  }, [pickerJob, subjects]);

  const applySubject = async (subjectId) => {
    setPickerErr(""); setSavingPick(true);
    try {
      await api.post(`/subjects/${subjectId}/clone`, { job_id: pickerJob.id });
      setPickerJob(null); setPickerSearch("");
      load();
    } catch (e) {
      setPickerErr(formatErr(e));
    } finally {
      setSavingPick(false);
    }
  };

  return (
    <Page
      title="Jobs"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Jobs"]}
      loading={loading && items.length === 0}
      loadingSkeleton={
        <div className="space-y-3">
          <Skeleton.Card />
          <Skeleton.Card />
          <Skeleton.Card />
        </div>
      }
      actions={isContractor ? null : <button className="btn btn-primary" onClick={() => { setForm({}); setErr(""); setOpen(true); }} data-testid="new-job-btn"><Plus size={14} /> NEW JOB</button>}
      testid="jobs-page"
    >
      <UpgradeBanner orgType={user?.org_type} feature="create your own jobs" testid="jobs-upgrade-banner" />

      {/* iter 35 — Typeahead filter bar (case / customer / subject / location / assignee). */}
      <div className="surface p-3 mb-3" data-testid="jobs-filter-bar">
        <div className="flex items-center justify-between mb-2">
          <div className="label-xs">FILTER JOBS</div>
          {anyFilter && (
            <button className="btn btn-ghost btn-sm" onClick={clearAllFilters} data-testid="jobs-filters-clear-all">
              CLEAR ALL
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
          <CaseSearchInput
            value={filters.case?.id || null}
            valueLabel={filters.case?.label || null}
            onSelect={(c) => setFilters(f => ({ ...f, case: { id: c.id, label: c.case_number || c.plaintiff || c.id.slice(0, 8) } }))}
            onClear={() => clearFilter("case")}
            placeholder="Case…"
            testid="jobs-filter-case"
          />
          <CustomerSearchInput
            value={filters.customer?.id || null}
            valueLabel={filters.customer?.label || null}
            onSelect={(c) => setFilters(f => ({ ...f, customer: { id: c.id, label: c.name } }))}
            onClear={() => clearFilter("customer")}
            placeholder="Customer…"
            testid="jobs-filter-customer"
          />
          <SubjectSearchInput
            value={filters.subject?.id || null}
            valueLabel={filters.subject?.label || null}
            onSelect={(s) => setFilters(f => ({ ...f, subject: { id: s.id, label: [s.first_name, s.last_name].filter(Boolean).join(" ") || s.aka || "(unnamed)" } }))}
            onClear={() => clearFilter("subject")}
            placeholder="Subject…"
            testid="jobs-filter-subject"
          />
          <LocationSearchInput
            value={filters.location?.id || null}
            valueLabel={filters.location?.label || null}
            onSelect={(l) => setFilters(f => ({ ...f, location: { id: l.id, label: l.name } }))}
            onClear={() => clearFilter("location")}
            placeholder="Location…"
            testid="jobs-filter-location"
          />
          <UserSearchInput
            value={filters.assignee?.id || null}
            valueLabel={filters.assignee?.label || null}
            onSelect={(uRow) => setFilters(f => ({ ...f, assignee: { id: uRow.id, label: uRow.name } }))}
            onClear={() => clearFilter("assignee")}
            placeholder="Assigned to…"
            testid="jobs-filter-assignee"
          />
        </div>
      </div>

      <div className="surface">
        {filteredItems.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="empty-jobs">
            <Briefcase size={32} weight="thin" className="mx-auto mb-3" />
            {anyFilter
              ? "No jobs match the current filters."
              : isContractor
                ? "No jobs yet. Jobs assigned to you by a host org will appear here."
                : "No jobs yet. Create a case first, then add a job."}
          </div>
        ) : (
          <table className="dense">
            <thead><tr>
              <th className="w-8"><input {...bulk.allProps()} /></th>
              <th>JOB #</th><th>CASE</th><th>TYPE</th><th>STATUS</th><th>ASSIGNED</th><th>SUBJ</th><th>DUE</th>
            </tr></thead>
            <tbody>
              {filteredItems.map(j => {
                const subjN = subjectsByJob[j.id] || 0;
                return (
                  <tr key={j.id} data-testid={`job-row-${j.id}`}>
                    <td><input {...bulk.rowProps(j.id)} /></td>
                    <td className="mono">
                      {/* iter 37 — first column links to the most detailed view (Job Dash page). */}
                      <Link
                        to={`/job-dash/${j.id}`}
                        className="link-cell"
                        data-testid={`job-row-open-${j.id}`}
                        title="Open full Job Dash"
                      >
                        {j.job_number || j.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="text-xs">{cases.find(c => c.id === j.case_id)?.case_number || "—"}</td>
                    <td>{types.find(t => t.id === j.job_type_id)?.name || "—"}</td>
                    <td>{statuses.find(s => s.id === j.job_status_id)?.name || "—"}</td>
                    <td>{users.find(u => u.id === j.assigned_user_id)?.name || "—"}</td>
                    <td className="mono text-xs">
                      {/* SUBJ cell stays as a quick-glance mini-panel trigger. */}
                      <button
                        className="link-cell"
                        onClick={() => setDetailJobId(j.id)}
                        data-testid={`open-mini-panel-${j.id}`}
                        title="Quick mini-panel preview"
                      >
                        {subjN || "—"}
                      </button>
                    </td>
                    <td className="text-xs text-muted-foreground">{j.due_date ? new Date(j.due_date).toLocaleDateString() : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title="New job" size="narrow" testid="new-job-drawer"
        footer={<><button className="btn btn-secondary" onClick={() => setOpen(false)}>CANCEL</button><button className="btn btn-primary" onClick={submit} data-testid="save-job-btn"><Plus size={14} /> CREATE</button></>}>
        <p className="text-xs text-muted-foreground mb-3">
          Capture the basics here — you&apos;ll be taken straight to the Job Dash to fill in case details, service locations, and the rest.
        </p>

        {/* a. CUSTOMER ----------------------------------------------------- */}
        <Field label="CUSTOMER">
          <CustomerSearchInput
            value={form.customer_id}
            valueLabel={(() => {
              const c = customers.find(x => x.id === form.customer_id);
              return c ? `${c.name}${c.email ? ` · ${c.email}` : ""}` : null;
            })()}
            onSelect={(c) => setForm(f => ({ ...f, customer_id: c.id }))}
            onClear={() => setForm(f => ({ ...f, customer_id: null }))}
            onCreateNew={() => { clearSub(); setNewCust({ name: "", email: "", phone: "", address: "", tags: [] }); }}
            testid="job-customer-search"
          />
        </Field>

        {/* b. JOB TYPE (search-style picker + inline create) ----------------- */}
        <Field label="JOB TYPE">
          <div className="flex gap-2">
            <select
              className="input flex-1"
              value={form.job_type_id || ""}
              onChange={e => setForm(f => ({ ...f, job_type_id: e.target.value, service_speed_preset_id: null }))}
              data-testid="job-type-select"
            >
              <option value="">— pick a job type —</option>
              {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button
              type="button"
              className="btn btn-secondary btn-sm whitespace-nowrap"
              onClick={async () => {
                const name = prompt("New job type name");
                if (!name) return;
                try {
                  const r = await api.post("/jobs/types", { name, apply_case: true, has_location_names: false });
                  setTypes(arr => [...arr, r.data]);
                  setForm(f => ({ ...f, job_type_id: r.data.id }));
                } catch (e) { alert(formatErr(e)); }
              }}
              data-testid="job-add-type-btn"
            >
              <Plus size={12} /> NEW
            </button>
          </div>
        </Field>

        {/* c. SERVICE SPEED (only when a Job Type is selected) -------------- */}
        {form.job_type_id && (
          <Field label="SERVICE SPEED">
            {speedPresets.length === 0 ? (
              <div className="text-xs text-muted-foreground italic" data-testid="speed-empty">
                No speed presets for this type. Set them up under Templates → Service Speed Presets.
              </div>
            ) : (
              <select
                className="input"
                value={form.service_speed_preset_id || ""}
                onChange={e => setForm(f => ({ ...f, service_speed_preset_id: e.target.value }))}
                data-testid="job-service-speed-select"
              >
                <option value="">—</option>
                {speedPresets.map(p => {
                  const tail = [
                    p.days_out_for_soft_due_date != null ? `soft ${p.days_out_for_soft_due_date}d` : null,
                    p.days_out_for_hard_due_date != null ? `hard ${p.days_out_for_hard_due_date}d` : null,
                  ].filter(Boolean).join(" · ");
                  return <option key={p.id} value={p.id}>{p.name}{tail ? ` (${tail})` : ""}</option>;
                })}
              </select>
            )}
          </Field>
        )}

        {/* d. NOTES --------------------------------------------------------- */}
        <Field label="NOTES">
          <div className="flex gap-2 mb-1">
            <div className="flex-1" />
            <button type="button" className="btn btn-secondary btn-sm whitespace-nowrap"
              onClick={openNotePicker}
              data-testid="job-add-note-template-btn"><FileText size={12} /> PICK TEMPLATE</button>
          </div>
          <textarea className="input" rows={3} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </Field>

        {/* e. TAGS (multi-select; pulls org-wide job-category tags) -------- */}
        {/* iter 42 — switched to the shared <TagPicker> used in Vendors/Customers
            drawers for a consistent UX (chip tray + dropdown + inline NEW TAG). */}
        <TagPicker
          category="job"
          value={form.tags || []}
          onChange={(tags) => setForm(f => ({ ...f, tags }))}
          testid="job-tag-picker"
        />

        {/* f. .EML upload — handed to the existing intake pipeline post-save */}
        <Field label="EMAIL FILE (.eml) — OPTIONAL">
          <p className="text-[10px] text-muted-foreground mb-2">
            Drop an .eml here and after the job is created it&apos;ll go to Intake → Processing for AI review, and the job will be tagged <span className="kbd">needs-review</span>.
          </p>
          <input
            type="file"
            accept=".eml,message/rfc822"
            onChange={e => setForm(f => ({ ...f, emlFile: e.target.files?.[0] || null }))}
            className="text-xs"
            data-testid="job-eml-file"
          />
          {form.emlFile && (
            <div className="text-[11px] text-muted-foreground mt-1" data-testid="job-eml-file-name">
              Queued: {form.emlFile.name} ({Math.round(form.emlFile.size / 1024)} KB)
            </div>
          )}
        </Field>

        {err && <div className="text-xs text-destructive mt-2">{err}</div>}
      </Drawer>

      {/* ---- Subject picker drawer: pull from previous job into this one ---- */}
      <Drawer
        open={!!pickerJob}
        onClose={() => setPickerJob(null)}
        size="medium"
        testid="subject-picker-drawer"
        title={pickerJob
          ? `Add subject to ${pickerJob.job_number || "JOB-" + pickerJob.id.slice(0, 6)}`
          : ""}
        footer={
          <button className="btn btn-secondary" onClick={() => setPickerJob(null)}>CLOSE</button>
        }
      >
        {pickerJob && (
          <>
            <div className="text-xs text-muted-foreground mb-3">
              Pick a subject from any previous job to clone their name + description into this job.
              {pickerJob.case_id && (
                <> Subjects from <span className="mono">{cases.find(c => c.id === pickerJob.case_id)?.case_number || "this case"}</span> are listed first ({sameCaseCount} match{sameCaseCount === 1 ? "" : "es"}).</>
              )}
            </div>

            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                className="input pl-9"
                placeholder="Search by name, AKA, or notes…"
                value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
                data-testid="subject-picker-search"
              />
            </div>

            <div className="max-h-[420px] overflow-y-auto -mx-1" data-testid="subject-picker-list">
              {pickerSubjects.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-12">
                  {subjects.length === 0
                    ? "No subjects in your org yet. Go to Subjects to create one."
                    : "No matches. Try a different search term."}
                </div>
              ) : pickerSubjects.map(s => {
                const sameCase = s.case_id && s.case_id === pickerJob.case_id;
                const srcJob = items.find(x => x.id === s.job_id);
                return (
                  <div
                    key={s.id}
                    className={`flex items-center justify-between p-3 mx-1 my-1 border ${sameCase ? "border-primary/40 bg-primary/5" : "border-border"}`}
                    data-testid={`picker-row-${s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{subjectLabel(s)}</span>
                        {sameCase && (
                          <span className="text-[10px] mono uppercase px-2 py-0.5 bg-primary text-white" data-testid={`badge-samecase-${s.id}`}>
                            SAME CASE
                          </span>
                        )}
                        {s.aka && <span className="text-xs text-muted-foreground truncate">aka {s.aka}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mono mt-0.5">
                        {srcJob ? `from ${srcJob.job_number || "JOB-" + srcJob.id.slice(0, 6)}` : "no prior job"}
                        {s.date_of_birth ? ` · DOB ${s.date_of_birth}` : ""}
                        {s.ssn_last4 ? ` · SSN…${s.ssn_last4}` : ""}
                      </div>
                      {s.notes && <div className="text-xs text-muted-foreground truncate mt-1">{s.notes}</div>}
                    </div>
                    <button
                      className="btn btn-primary btn-sm ml-3 flex-shrink-0"
                      onClick={() => applySubject(s.id)}
                      disabled={savingPick}
                      data-testid={`use-subject-${s.id}`}
                    >
                      USE
                    </button>
                  </div>
                );
              })}
            </div>

            {pickerErr && <div className="text-xs text-destructive mt-3" data-testid="picker-error">{pickerErr}</div>}
          </>
        )}
      </Drawer>

      {/* ---- Job Mini-Panel: opened by clicking the SUBJ count ---- */}
      <JobMiniPanel
        jobId={detailJobId}
        allLocations={locations}
        onClose={() => setDetailJobId(null)}
        onJobChange={load}
        onSwitchJob={(id) => setDetailJobId(id)}
        onDuplicated={(newJob) => { load(); setDetailJobId(newJob.id); }}
      />

      {/* ============================================================
       *  Phase 3 — stacked sub-drawers from inside the New Job form
       * ============================================================ */}

      {/* + NEW Case */}
      <Drawer
        open={!!newCase}
        onClose={() => setNewCase(null)}
        size="narrow"
        testid="job-new-case-drawer"
        title="New case"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setNewCase(null)}>CANCEL</button>
          <button className="btn btn-primary" onClick={saveNewCase} disabled={subBusy} data-testid="job-new-case-save">
            {subBusy ? "SAVING…" : "SAVE & USE"}
          </button>
        </>}>
        <p className="text-xs text-muted-foreground mb-3">The case will be created and auto-selected on the job behind this panel.</p>
        <Field label="CASE NUMBER"><input className="input" autoFocus
          value={newCase?.case_number || ""}
          onChange={e => setNewCase({ ...newCase, case_number: e.target.value })}
          data-testid="job-new-case-number" /></Field>
        <Field label="PLAINTIFF"><input className="input"
          value={newCase?.plaintiff || ""}
          onChange={e => setNewCase({ ...newCase, plaintiff: e.target.value })} /></Field>
        <Field label="DEFENDANT"><input className="input"
          value={newCase?.defendant || ""}
          onChange={e => setNewCase({ ...newCase, defendant: e.target.value })} /></Field>
        {subErr && <div className="text-xs text-destructive mt-2 flex items-center gap-1"><Warning size={12} /> {subErr}</div>}
      </Drawer>

      {/* + NEW Service Location — uses the UNIFIED LocationManagerDrawer
          (kind='service'). Same high-fidelity form (Google Places autocomplete,
          contact, lat/lng) as the office-location flow in OrgAdmin. */}
      <LocationManagerDrawer
        open={!!newLoc}
        onClose={() => setNewLoc(null)}
        onSuccess={onNewLocationSaved}
        mode="create"
        initialData={null}
        locationKind="service"
      />

      {/* + NEW Customer — uses the reusable CustomerFormDrawer so this
          surface stays in lockstep with the Organizations / Invoices flows.
          On save, onNewCustomerSaved auto-links the new customer to this job. */}
      <CustomerFormDrawer
        open={!!newCust}
        onClose={() => { setNewCust(null); clearSub(); }}
        customer={null}
        subtitle="Creates a customer in your records and links them to this job."
        onSaved={onNewCustomerSaved}
      />

      {/* + NEW Assignee — toggle Internal User vs Contractor Org */}
      <Drawer
        open={!!newAssignee}
        onClose={() => { setNewAssignee(null); clearSub(); }}
        size="narrow"
        testid="job-new-assignee-drawer"
        title="New assignee"
        footer={<>
          <button className="btn btn-secondary" onClick={() => { setNewAssignee(null); clearSub(); }}>CANCEL</button>
          <button
            className="btn btn-primary"
            onClick={saveNewAssignee}
            disabled={subBusy
              || (newAssignee?.kind === ASSIGNEE_KIND_USER ? !newAssignee?.email
                  : !newAssignee?.org_name)}
            data-testid="job-new-assignee-save"
          >
            {subBusy ? "SAVING…" : (newAssignee?.kind === ASSIGNEE_KIND_USER ? "SEND INVITE" : "ADD VENDOR")}
          </button>
        </>}>
        {newAssignee && (
          <>
            <div className="flex gap-1 mb-4" data-testid="assignee-kind-toggle">
              <button type="button"
                className={`btn btn-sm flex-1 ${newAssignee.kind === ASSIGNEE_KIND_USER ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setNewAssignee({ ...newAssignee, kind: ASSIGNEE_KIND_USER })}
                data-testid="assignee-kind-user"
              >INTERNAL USER</button>
              <button type="button"
                className={`btn btn-sm flex-1 ${newAssignee.kind === ASSIGNEE_KIND_CONTRACTOR ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setNewAssignee({ ...newAssignee, kind: ASSIGNEE_KIND_CONTRACTOR })}
                data-testid="assignee-kind-contractor"
              >CONTRACTOR ORG</button>
            </div>

            {newAssignee.kind === ASSIGNEE_KIND_USER ? (
              <>
                <p className="text-xs text-muted-foreground mb-3">
                  Invites a new teammate to join <strong>your</strong> organization.
                  They&apos;ll appear in the Assigned-To dropdown once they accept.
                </p>
                <Field label="EMAIL">
                  <input className="input" type="email" autoFocus
                    value={newAssignee.email || ""}
                    onChange={(e) => setNewAssignee({ ...newAssignee, email: e.target.value })}
                    data-testid="assignee-user-email"
                  />
                </Field>
                <Field label="ROLE">
                  <select className="input"
                    value={newAssignee.role || "dispatcher"}
                    onChange={(e) => setNewAssignee({ ...newAssignee, role: e.target.value })}
                    data-testid="assignee-user-role"
                  >
                    <option value="admin">admin</option>
                    <option value="dispatcher">dispatcher</option>
                    <option value="driver">driver</option>
                    <option value="accountant">accountant</option>
                  </select>
                </Field>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3">
                  Adds a contractor org to your <strong>Vendors</strong> page.
                  The invite toggle below is off by default — flip it on to notify them now.
                </p>
                <Field label="VENDOR ORG NAME">
                  <input className="input" autoFocus
                    value={newAssignee.org_name || ""}
                    onChange={(e) => setNewAssignee({ ...newAssignee, org_name: e.target.value })}
                    data-testid="assignee-vendor-name"
                  />
                </Field>
                <Field label="PRIMARY CONTACT NAME">
                  <input className="input"
                    value={newAssignee.contact_name || ""}
                    onChange={(e) => setNewAssignee({ ...newAssignee, contact_name: e.target.value })}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CONTACT EMAIL">
                    <input className="input" type="email"
                      value={newAssignee.contact_email || ""}
                      onChange={(e) => setNewAssignee({ ...newAssignee, contact_email: e.target.value })}
                    />
                  </Field>
                  <Field label="CONTACT PHONE">
                    <input className="input"
                      value={newAssignee.contact_phone || ""}
                      onChange={(e) => setNewAssignee({ ...newAssignee, contact_phone: e.target.value })}
                    />
                  </Field>
                </div>
                <TagPicker
                  category="vendor"
                  value={newAssignee.tags || []}
                  onChange={(tags) => setNewAssignee(prev => ({ ...prev, tags }))}
                  testid="assignee-vendor-tag-picker"
                />
                <label className="flex items-center gap-2 mt-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!newAssignee.send_invite}
                    onChange={(e) => setNewAssignee({ ...newAssignee, send_invite: e.target.checked })}
                    data-testid="assignee-vendor-send-invite"
                  />
                  <span className="text-sm">Send the vendor an invite link now (default OFF)</span>
                </label>
              </>
            )}

            {subOk && <div className="text-xs text-green-600 mt-3 flex items-center gap-1" data-testid="assignee-ok"><CheckCircle size={12} weight="fill" /> {subOk}</div>}
            {subErr && <div className="text-xs text-destructive mt-3 flex items-center gap-1"><Warning size={12} /> {subErr}</div>}
          </>
        )}
      </Drawer>

      {/* + NEW Note Template picker */}
      <Drawer
        open={notePicker}
        onClose={() => { setNotePicker(false); setTplNew(null); clearSub(); }}
        size="narrow"
        testid="job-note-template-drawer"
        title="Note templates"
        footer={
          <button className="btn btn-secondary" onClick={() => { setNotePicker(false); setTplNew(null); clearSub(); }}>CLOSE</button>
        }
      >
        <p className="text-xs text-muted-foreground mb-3">
          Pick a saved note to append to the NOTES field. Create new ones with <strong>+ NEW TEMPLATE</strong>.
        </p>

        {!tplNew && (
          <button className="btn btn-secondary btn-sm mb-3"
            onClick={() => { clearSub(); setTplNew({ title: "", body: "" }); }}
            data-testid="job-note-tpl-new-btn">
            <Plus size={12} /> NEW TEMPLATE
          </button>
        )}

        {tplNew && (
          <div className="surface p-3 mb-3 space-y-2" data-testid="job-note-tpl-create-form">
            <Field label="TITLE"><input className="input" autoFocus
              value={tplNew.title}
              onChange={(e) => setTplNew({ ...tplNew, title: e.target.value })}
              data-testid="job-note-tpl-title" /></Field>
            <Field label="BODY"><textarea className="input" rows={4}
              value={tplNew.body}
              onChange={(e) => setTplNew({ ...tplNew, body: e.target.value })}
              data-testid="job-note-tpl-body" /></Field>
            <div className="flex justify-end gap-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setTplNew(null)}>CANCEL</button>
              <button className="btn btn-primary btn-sm"
                onClick={saveNewTemplate}
                disabled={subBusy || !tplNew.title.trim() || !tplNew.body.trim()}
                data-testid="job-note-tpl-save">
                {subBusy ? "…" : "SAVE TEMPLATE"}
              </button>
            </div>
            {subErr && <div className="text-xs text-destructive">{subErr}</div>}
          </div>
        )}

        {templates.length === 0 && !tplNew && (
          <div className="text-xs text-muted-foreground py-8 text-center" data-testid="job-note-tpl-empty">
            No templates yet. Create one to reuse later.
          </div>
        )}

        <ul className="space-y-2" data-testid="job-note-tpl-list">
          {templates.map(t => (
            <li key={t.id} className="surface p-3" data-testid={`job-note-tpl-row-${t.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-sm">{t.title}</div>
                <div className="flex gap-1">
                  <button className="btn btn-primary btn-sm"
                    onClick={() => pickTemplate(t)}
                    data-testid={`job-note-tpl-use-${t.id}`}>USE</button>
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => deleteTemplate(t.id)}
                    data-testid={`job-note-tpl-delete-${t.id}`}>
                    <Trash size={12} />
                  </button>
                </div>
              </div>
              <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans">{t.body}</pre>
            </li>
          ))}
        </ul>
      </Drawer>
    </Page>
  );
}
